'use client';

import { buildWebExpProperties } from '@nextmar/snowplow-sdk';
import { useCallback, useEffect, useRef } from 'react';
import { useOnInView } from 'react-intersection-observer';
import { captureAnalyticsEvent } from '@/reporting/earlyEvents';
import logger from '@/utils/client-logger';

/** 当前 ts 的早期曝光接收状态。 */
interface WebExpAttemptState {
  /** 当前 ts 是否已被门面接受。 */
  current: boolean;
}

/**
 * 独立尝试一次 SDK WebExp，不让任何 SDK 异常改变业务曝光回调语义。
 * @param target 当前定时器对应的真实观察节点。
 * @param attemptedState 当前 ts 的 SDK 尝试状态。
 */
function attemptTrackWebExp(target: Element, attemptedState: WebExpAttemptState): void {
  if (attemptedState.current) return;

  try {
    // 在可见条件满足时提取 DOM，门面只接收可序列化的元素快照。
    const properties = buildWebExpProperties(target);
    if (!properties) return;
    const result = captureAnalyticsEvent({ kind: 'webExp', properties });
    // 只有门面已接收才提交 once；拒绝后下一次有效可见仍可重试。
    attemptedState.current = result.accepted === true;
  } catch {
    // SDK Client、DOM helper 或同步发送异常均不得阻断业务曝光。
  }
}

/**
 * 有效曝光配置选项
 */
export interface UseValidExposureOptions {
  /**
   * 有效曝光回调函数，无参数
   */
  callback: () => void;
  /**
   * 曝光阈值（0-1），默认 0.8（80%）
   */
  threshold?: number;
  /**
   * 最小停留时长（毫秒），默认 500ms
   */
  minDuration?: number;
  /**
   * 时间戳(商品sku)
   */
  ts?: string | number;
}

/**
 * Hook 返回值
 */
export interface UseValidExposureReturn {
  /**
   * 需要绑定到目标元素的 ref
   */
  ref: (node?: Element | null) => void;
}

/**
 * 使用 useOnInView 回调式 API，无 state 更新，避免分页后向下滚动 inView 不更新的问题。
 */
export function useValidExposure(options: UseValidExposureOptions): UseValidExposureReturn {
  const { callback, threshold = 0.8, minDuration = 500, ts } = options;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);
  const isExposedRef = useRef(false);
  const webExpAttemptedRef = useRef(false);
  const nodeRef = useRef<Element | null>(null);
  const inViewRefCallback = useRef<((node: Element | null) => void) | null>(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const handleIntersection = useCallback(
    (inView: boolean, entry: IntersectionObserverEntry) => {
      const ratio = entry?.intersectionRatio ?? 0;
      const isIntersecting = inView && ratio >= threshold;
      if (isIntersecting) {
        if (isExposedRef.current && webExpAttemptedRef.current) return;
        if (timerRef.current) clearTimeout(timerRef.current);
        const exposureTarget = entry.target;
        timerRef.current = setTimeout(() => {
          // 业务回调可能同步导航或修改 DOM，曝光必须在它之前冻结。
          attemptTrackWebExp(exposureTarget, webExpAttemptedRef);
          if (!isExposedRef.current) {
            try {
              isExposedRef.current = true;
              callbackRef.current();
            } catch (error) {
              logger.log('[useValidExposure] Callback error:', error);
              isExposedRef.current = false;
            }
          }
          timerRef.current = null;
        }, minDuration);
      } else if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    },
    [threshold, minDuration],
  );

  const inViewRef = useOnInView(handleIntersection, {
    threshold,
    triggerOnce: false,
  });

  // 保存 inViewRef 的引用，用于 ts 变化时重新 observe
  useEffect(() => {
    inViewRefCallback.current = inViewRef;
  }, [inViewRef]);

  // ts 变化时：清定时器与曝光标记，强制重新 observe
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    isExposedRef.current = false;
    webExpAttemptedRef.current = false;

    // 强制重新 observe：先 unobserve，再重新 observe
    if (nodeRef.current && inViewRefCallback.current) {
      const currentNode = nodeRef.current;
      inViewRefCallback.current(null); // unobserve
      // 立即重新 observe，IntersectionObserver 会立即触发一次检查
      inViewRefCallback.current(currentNode); // 重新 observe
    }
  }, [ts]);

  // 包装 ref：保存 node 引用
  const ref = useCallback(
    (node: Element | null) => {
      nodeRef.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return { ref };
}
